import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { LayoutDashboard, Car, History, CalendarDays, ClipboardList, Bell, Navigation, User, Loader2, AlertCircle, Plus, X, ChevronDown, CheckCircle2, Clock, FileText, Tag, Gauge, Hash, CheckCircle, Wrench, ArrowRight, ChevronRight, Eye, ChevronLeft } from 'lucide-react';

import VehicleFormUnified, { validateVehicleFields, VEHICLE_FORM_DEFAULTS } from '../components/VehicleFormUnified';
import Icon from '../components/AppIcon';


// ─── Shared constants ────────────────────────────────────────────────────────

const SEGUIMIENTO_STAGES = [
  { key: 'recepcionado', label: 'Recibido' },
  { key: 'inspeccionado', label: 'Inspeccionado' },
  { key: 'cotizando', label: 'Cotizado' },
  { key: 'aprobado', label: 'Autorizado' },
  { key: 'enproceso', label: 'En proceso' },
  { key: 'terminado', label: 'Listo para entrega' },
  { key: 'entregado', label: 'Entregado' },
];
const STAGE_ORDER = ['recepcionado', 'inspeccionado', 'cotizando', 'aprobado', 'enproceso', 'terminado', 'entregado'];
const getStageIndex = (estado) => STAGE_ORDER?.indexOf(estado);

const estadoColors = {
  recepcionado: 'bg-gray-100 text-gray-700',
  inspeccionado: 'bg-blue-100 text-blue-700',
  cotizando: 'bg-yellow-100 text-yellow-700',
  aprobado: 'bg-purple-100 text-purple-700',
  enproceso: 'bg-orange-100 text-orange-700',
  terminado: 'bg-green-100 text-green-700',
  entregado: 'bg-emerald-100 text-emerald-700',
  cancelado: 'bg-red-100 text-red-700',
};
const estadoLabels = {
  recepcionado: 'Recibido',
  inspeccionado: 'Inspeccionado',
  cotizando: 'Cotizado',
  aprobado: 'Autorizado',
  enproceso: 'En proceso',
  terminado: 'Listo para entrega',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

const SERVICE_TYPES = ['Mantenimiento general', 'Cambio de llantas', 'Revisión', 'Frenos', 'Alineación y balanceo', 'Otro'];
const TIME_SLOTS = ['8:00', '9:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'];
const REQUEST_TYPES = ['Servicio mecánico', 'Revisión general', 'Cotización', 'Compra de llantas', 'Refacciones', 'Asistencia vial', 'Recolección y entrega', 'Otro'];

// ─── REGISTRAR UNIDAD MODAL ───────────────────────────────────────────────────

const RegistrarUnidadModal = ({ portalUser, onClose, onCreated }) => {
  const [formData, setFormData] = useState({ ...VEHICLE_FORM_DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors?.[field]) setFieldErrors(prev => { const n = { ...prev }; delete n?.[field]; return n; });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validateVehicleFields(formData);
    if (Object.keys(errs)?.length > 0) { setFieldErrors(errs); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        client_id: portalUser?.id,
        company_id: portalUser?.company_id,
        tipo: formData?.vehicleType || 'auto',
        placas: formData?.plates?.trim() || null,
        marca: formData?.brand?.trim(),
        modelo: formData?.model?.trim(),
        year: formData?.year ? parseInt(formData?.year, 10) : null,
        combustible: formData?.fuelType || null,
        kilometraje: formData?.odometer ? parseInt(formData?.odometer, 10) : null,
        color: formData?.color || null,
        motor: formData?.engine?.trim() || null,
      };
      const vinTrimmed = formData?.vin?.trim();
      if (vinTrimmed) payload.vin = vinTrimmed;
      const { data, error: insertErr } = await supabase?.from('vehicles')?.insert(payload)?.select()?.single();
      if (insertErr) {
        setError('No se pudo registrar la unidad: ' + (insertErr?.message || 'Error desconocido'));
        return;
      }
      onCreated?.(data);
    } catch (err) {
      setError('Error inesperado: ' + (err?.message || 'Intenta de nuevo'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-base font-bold text-gray-900">Registrar nueva unidad</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <VehicleFormUnified formData={formData} onChange={handleChange} errors={fieldErrors} />
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Guardando...</> : 'Guardar unidad'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── NAV ITEMS ────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Inicio', icon: LayoutDashboard },
  { key: 'vehiculos', label: 'Vehículos', icon: Car },
  { key: 'historial', label: 'Historial', icon: History },
  { key: 'citas', label: 'Citas', icon: CalendarDays },
  { key: 'solicitudes', label: 'Solicitudes', icon: ClipboardList },
  { key: 'notificaciones', label: 'Notificaciones', icon: Bell },
  { key: 'seguimiento', label: 'Seguimiento', icon: Navigation },
  { key: 'perfil', label: 'Perfil', icon: User },
];

// ─── DASHBOARD VIEW ───────────────────────────────────────────────────────────

const DashboardView = ({ portalUser, setActiveView, onRegresar }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const [stats, setStats] = useState({ vehicles: 0, upcomingAppointments: 0, nearestAppointmentDate: null, activeOTs: 0, pendingRequests: 0 });
  const [recentOTs, setRecentOTs] = useState([]);
  const [activeOTs, setActiveOTs] = useState([]);
  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    if (portalUser?.id) {
      loadDashboard();
    } else {
      // No client id available — stop spinner immediately, never hang
      setLoading(false);
    }
  }, [portalUser?.id]);

  const withTimeout = (promise, ms = 10000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('PORTAL_POST_LOGIN_DASHBOARD_LOAD_FAILED: timeout')), ms)
      ),
    ]);

  const loadDashboard = async () => {
    setLoading(true);
    setDashboardError(null);
    try {
      const clientId = portalUser?.id;
      const companyId = portalUser?.company_id;
      const todayStr = new Date()?.toISOString()?.split('T')?.[0];

      // Diagnostic log — confirms which client_id is being used
      console.log('[DashboardView] Loading for clientId:', clientId, 'companyId:', companyId);

      // Per-query timeout so one hanging query can't block the whole dashboard
      const qTimeout = (promise, ms = 8000) =>
        Promise.race([
          promise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('query_timeout')), ms)
          ),
        ]);

      const [
        vehiclesRes,
        apptRes,
        activeOTRes,
        recentOTRes,
        requestRes,
        promosRes,
      ] = await Promise.allSettled([
        // Vehicles count
        qTimeout(supabase?.from('vehicles')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('client_id', clientId)),
        // Upcoming appointments
        qTimeout(supabase
          ?.from('client_appointments')
          ?.select('appointment_date, appointment_time', { count: 'exact' })
          ?.eq('client_id', clientId)
          ?.gte('appointment_date', todayStr)
          ?.neq('status', 'cancelada')
          ?.order('appointment_date', { ascending: true })
          ?.order('appointment_time', { ascending: true })),
        // Active work orders
        qTimeout(supabase
          ?.from('work_orders')
          ?.select('id, folio_ot, estado_ot, fecha_ingreso, motivo_ingreso, observaciones, vehicle_id, vehicles(marca, modelo, placas, year)')
          ?.eq('client_id', clientId)
          ?.not('estado_ot', 'in', '(cerrada,entregada,cancelado,entregado,cancelada)')),
        // Recent work orders
        qTimeout(supabase
          ?.from('work_orders')
          ?.select('id, folio_ot, estado_ot, fecha_ingreso, motivo_ingreso, vehicle_id, vehicles(marca, modelo, placas, year)')
          ?.eq('client_id', clientId)
          ?.order('fecha_ingreso', { ascending: false })
          ?.limit(5)),
        // Pending service requests count
        qTimeout(supabase
          ?.from('service_requests')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('client_id', clientId)
          ?.in('status', ['nueva', 'pendiente', 'en revisión'])),
        // Promotions — wrapped with extra safety since table may not exist
        qTimeout(supabase?.from('promotions')?.select('*')?.eq('company_id', companyId)?.eq('is_active', true)?.limit(4), 5000),
      ]);

      // Diagnostic: log raw results to identify which queries fail
      console.log('[DashboardView] vehicles:', vehiclesRes?.status, vehiclesRes?.value?.count, vehiclesRes?.value?.error?.message);
      console.log('[DashboardView] appointments:', apptRes?.status, apptRes?.value?.count, apptRes?.value?.error?.message);
      console.log('[DashboardView] activeOTs:', activeOTRes?.status, activeOTRes?.value?.data?.length, activeOTRes?.value?.error?.message);
      console.log('[DashboardView] recentOTs:', recentOTRes?.status, recentOTRes?.value?.data?.length, recentOTRes?.value?.error?.message);
      console.log('[DashboardView] requests:', requestRes?.status, requestRes?.value?.count, requestRes?.value?.error?.message);

      const vehicleCount = vehiclesRes?.status === 'fulfilled' ? (vehiclesRes?.value?.count ?? 0) : 0;
      const apptCount = apptRes?.status === 'fulfilled' ? (apptRes?.value?.count ?? 0) : 0;
      const apptData = apptRes?.status === 'fulfilled' ? (apptRes?.value?.data || []) : [];
      const nearestAppt = apptData?.[0] || null;
      const activeOTData = activeOTRes?.status === 'fulfilled' ? (activeOTRes?.value?.data || []) : [];
      const activeOTCount = activeOTData?.length;
      const recentOTData = recentOTRes?.status === 'fulfilled' ? (recentOTRes?.value?.data || []) : [];
      const requestCount = requestRes?.status === 'fulfilled' ? (requestRes?.value?.count ?? 0) : 0;
      const promos = promosRes?.status === 'fulfilled' ? (promosRes?.value?.data || []) : [];

      setStats({
        vehicles: vehicleCount,
        upcomingAppointments: apptCount,
        nearestAppointmentDate: nearestAppt,
        activeOTs: activeOTCount,
        pendingRequests: requestCount,
      });
      setRecentOTs(recentOTData);
      setActiveOTs(activeOTData?.slice(0, 3));
      setPromotions(promos);
    } catch (err) {
      console.error('[DashboardView] Error:', err);
      setDashboardError('PORTAL_POST_LOGIN_DASHBOARD_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  if (dashboardError) return (
    <div className="flex flex-col items-center justify-center min-h-64 gap-4 p-6">
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
        <AlertCircle className="w-6 h-6 text-red-600" />
      </div>
      <div className="text-center">
        <p className="text-gray-800 font-semibold">No se pudo cargar el portal</p>
        <p className="text-gray-500 text-sm mt-1">Verifica tu conexión e intenta de nuevo.</p>
        <p className="text-xs text-gray-400 mt-2 font-mono">{dashboardError}</p>
      </div>
      <button
        onClick={loadDashboard}
        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
      >
        Reintentar
      </button>
    </div>
  );

  const nearestDateLabel = stats?.nearestAppointmentDate
    ? new Date(stats.nearestAppointmentDate.appointment_date + 'T12:00:00')?.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
    : null;

  return (
    <div className="space-y-6">
      {onRegresar && (
        <div className="flex items-center justify-between py-1 mb-2 border-b border-gray-100">
          <button onClick={onRegresar} className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded">
            <ChevronLeft className="w-4 h-4" /><span>Regresar</span>
          </button>
          <span className="text-xs text-gray-400 italic">Ya estás en el inicio</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bienvenido, {portalUser?.nombre?.split(' ')?.[0]}</h1>
          <p className="text-gray-500 text-sm mt-0.5">Resumen de tu cuenta</p>
        </div>
        <button onClick={() => setActiveView('solicitudes')} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
          <Plus className="w-4 h-4" /><span className="hidden sm:inline">Solicitar servicio</span><span className="sm:hidden">Solicitar</span>
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div onClick={() => setActiveView('vehiculos')} className="bg-white rounded-xl p-4 border border-gray-200 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all">
          <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center mb-2">
            <Car className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.vehicles}</p>
          <p className="text-xs text-gray-500 mt-0.5">Vehículos registrados</p>
        </div>
        <div onClick={() => setActiveView('citas')} className="bg-white rounded-xl p-4 border border-gray-200 cursor-pointer hover:border-green-300 hover:shadow-sm transition-all">
          <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center mb-2">
            <CalendarDays className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.upcomingAppointments}</p>
          <p className="text-xs text-gray-500 mt-0.5">Próxima cita</p>
          {nearestDateLabel && <p className="text-xs text-green-600 font-medium mt-0.5">{nearestDateLabel}</p>}
        </div>
        <div onClick={() => setActiveView('seguimiento')} className="bg-white rounded-xl p-4 border border-gray-200 cursor-pointer hover:border-orange-300 hover:shadow-sm transition-all">
          <div className="w-9 h-9 bg-orange-50 rounded-lg flex items-center justify-center mb-2">
            <Clock className="w-5 h-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.activeOTs}</p>
          <p className="text-xs text-gray-500 mt-0.5">OTs activas</p>
        </div>
        <div onClick={() => setActiveView('solicitudes')} className="bg-white rounded-xl p-4 border border-gray-200 cursor-pointer hover:border-purple-300 hover:shadow-sm transition-all">
          <div className="w-9 h-9 bg-purple-50 rounded-lg flex items-center justify-center mb-2">
            <ClipboardList className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.pendingRequests}</p>
          <p className="text-xs text-gray-500 mt-0.5">Solicitudes pendientes</p>
        </div>
      </div>
      {activeOTs?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Clock className="w-4 h-4 text-orange-500" />Seguimiento de trabajo</h2>
            <button onClick={() => setActiveView('seguimiento')} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">Ver detalle <ChevronRight className="w-3 h-3" /></button>
          </div>
          <div className="divide-y divide-gray-50">
            {activeOTs?.map((ot) => {
              const currentIdx = getStageIndex(ot?.estado_ot);
              return (
                <div key={ot?.id} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{ot?.folio_ot || `OT-${ot?.id?.slice(0, 6)}`}</p>
                      <p className="text-xs text-gray-500 truncate">{ot?.vehicles?.marca} {ot?.vehicles?.modelo}{ot?.vehicles?.placas ? ` · ${ot?.vehicles?.placas}` : ''}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${estadoColors?.[ot?.estado_ot] || 'bg-gray-100 text-gray-700'}`}>{estadoLabels?.[ot?.estado_ot] || ot?.estado_ot}</span>
                  </div>
                  <div className="flex items-center gap-1 overflow-x-auto pb-1">
                    {SEGUIMIENTO_STAGES?.map((stage, idx) => {
                      const done = idx <= currentIdx;
                      const active = idx === currentIdx;
                      return (
                        <React.Fragment key={stage?.key}>
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${active ? 'bg-blue-600' : done ? 'bg-green-500' : 'bg-gray-200'}`}>
                              {done && !active ? <CheckCircle2 className="w-3 h-3 text-white" /> : <div className={`w-2 h-2 rounded-full ${active ? 'bg-white' : 'bg-gray-400'}`} />}
                            </div>
                            <p className={`text-[9px] mt-0.5 text-center leading-tight max-w-[44px] ${active ? 'text-blue-600 font-semibold' : done ? 'text-green-600' : 'text-gray-400'}`}>{stage?.label}</p>
                          </div>
                          {idx < SEGUIMIENTO_STAGES?.length - 1 && <div className={`flex-1 h-0.5 min-w-[8px] mb-4 ${idx < currentIdx ? 'bg-green-400' : 'bg-gray-200'}`} />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-5 py-3 border-t border-gray-50">
            <button onClick={() => setActiveView('seguimiento')} className="w-full flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium">Ver seguimiento completo <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Actividad reciente</h2>
          <button onClick={() => setActiveView('historial')} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">Ver todo <ChevronRight className="w-3 h-3" /></button>
        </div>
        {recentOTs?.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-400 text-sm">No hay órdenes de trabajo recientes</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentOTs?.map((ot) => (
              <div key={ot?.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{ot?.folio_ot || `OT-${ot?.id?.slice(0, 6)}`}</p>
                  <p className="text-xs text-gray-500 truncate">{ot?.vehicles?.marca} {ot?.vehicles?.modelo}{ot?.vehicles?.placas ? ` · ${ot?.vehicles?.placas}` : ''}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${estadoColors?.[ot?.estado_ot] || 'bg-gray-100 text-gray-700'}`}>{estadoLabels?.[ot?.estado_ot] || ot?.estado_ot}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {promotions?.length > 0 && (
        <div>
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Tag className="w-4 h-4 text-blue-600" />Promociones activas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {promotions?.map((promo) => (
              <div key={promo?.id} className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-4 text-white">
                <p className="font-semibold text-sm">{promo?.title}</p>
                {promo?.description && <p className="text-blue-100 text-xs mt-1 line-clamp-2">{promo?.description}</p>}
                {promo?.valid_until && <p className="text-blue-200 text-xs mt-2">Válido hasta: {new Date(promo.valid_until + 'T12:00:00')?.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}</p>}
                <button onClick={() => setActiveView('solicitudes')} className="mt-3 text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg transition-colors font-medium">Solicitar cita</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── VEHICULOS VIEW ───────────────────────────────────────────────────────────

const VehiculosView = ({ portalUser, setActiveView, setHistorialVehicleId, onRegresar }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRegistrarUnidad, setShowRegistrarUnidad] = useState(false);

  useEffect(() => {
    if (portalUser?.id) loadVehicles();
  }, [portalUser?.id]);

  const loadVehicles = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase?.from('vehicles')?.select('id, marca, modelo, year, placas, vin, kilometraje, color, created_at')?.eq('client_id', portalUser?.id)?.order('created_at', { ascending: false });
      if (err) { setError('No se pudieron cargar los vehículos.'); return; }
      setVehicles(data || []);
    } catch { setError('Error al cargar vehículos.'); } finally { setLoading(false); }
  };

  const handleVehicleCreated = (newVehicle) => {
    setShowRegistrarUnidad(false);
    loadVehicles();
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between py-1 mb-2 border-b border-gray-100">
        <button onClick={onRegresar} className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded">
          <ChevronLeft className="w-4 h-4" /><span>Regresar</span>
        </button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis Vehículos</h1>
          <p className="text-gray-500 text-sm mt-0.5">Todos los vehículos registrados en tu cuenta</p>
        </div>
        {vehicles?.length > 0 && (
          <button onClick={() => setShowRegistrarUnidad(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
            <Plus className="w-4 h-4" /><span className="hidden sm:inline">Nueva unidad</span><span className="sm:hidden">Agregar</span>
          </button>
        )}
      </div>
      {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
      {vehicles?.length === 0 && !error ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No tienes vehículos registrados</p>
          <p className="text-gray-400 text-sm mt-1 mb-4">Registra tu primera unidad para comenzar</p>
          <button onClick={() => setShowRegistrarUnidad(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
            <Plus className="w-4 h-4" />Nueva unidad
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {vehicles?.map((vehicle) => (
            <div key={vehicle?.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center"><Car className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="text-white font-bold text-base leading-tight">{vehicle?.marca} {vehicle?.modelo}</p>
                    <p className="text-blue-100 text-sm">{vehicle?.year || '—'}</p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Hash className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Placas</p><p className="text-sm font-semibold text-gray-900">{vehicle?.placas || '—'}</p></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Gauge className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Kilometraje</p><p className="text-sm font-semibold text-gray-900">{vehicle?.kilometraje ? `${Number(vehicle?.kilometraje)?.toLocaleString('es-MX')} km` : '—'}</p></div>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">VIN</p>
                  <p className="text-xs font-mono text-gray-700 truncate">{vehicle?.vin || '—'}</p>
                </div>
              </div>
              <div className="px-4 pb-4">
                <button onClick={() => { setHistorialVehicleId(vehicle?.id); setActiveView('historial'); }} className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors text-sm font-semibold">
                  <History className="w-4 h-4" />Ver historial
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showRegistrarUnidad && (
        <RegistrarUnidadModal
          portalUser={portalUser}
          onClose={() => setShowRegistrarUnidad(false)}
          onCreated={handleVehicleCreated}
        />
      )}
    </div>
  );
};

// ─── HISTORIAL VIEW ───────────────────────────────────────────────────────────

const HistorialView = ({ portalUser, initialVehicleId, onRegresar }) => {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(initialVehicleId || '');
  const [activeTab, setActiveTab] = useState(0);
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [selectedOT, setSelectedOT] = useState(null);

  const TABS = ['Historial', 'Servicios / OTs', 'Inspecciones', 'Cotizaciones'];

  const estadoOTColors = {
    recepcionado: 'bg-gray-100 text-gray-700',
    inspeccionado: 'bg-blue-100 text-blue-700',
    cotizando: 'bg-yellow-100 text-yellow-700',
    aprobado: 'bg-purple-100 text-purple-700',
    enproceso: 'bg-orange-100 text-orange-700',
    terminado: 'bg-green-100 text-green-700',
    entregado: 'bg-emerald-100 text-emerald-700',
    cancelado: 'bg-red-100 text-red-700',
  };
  const estadoOTLabels = {
    recepcionado: 'Recibido',
    inspeccionado: 'Inspeccionado',
    cotizando: 'Cotizado',
    aprobado: 'Autorizado',
    enproceso: 'En proceso',
    terminado: 'Listo para entrega',
    entregado: 'Entregado',
    cancelado: 'Cancelado',
  };
  const inspStatusColors = {
    bueno: 'bg-green-100 text-green-700',
    requiere_atencion: 'bg-yellow-100 text-yellow-700',
    critico: 'bg-red-100 text-red-700',
    no_aplica: 'bg-gray-100 text-gray-500',
  };
  const inspStatusLabels = {
    bueno: 'Bueno',
    requiere_atencion: 'Requiere atención',
    critico: 'Crítico',
    no_aplica: 'No aplica',
  };
  const cotStatusColors = {
    draft: 'bg-gray-100 text-gray-700',
    enviada: 'bg-blue-100 text-blue-700',
    aprobada: 'bg-green-100 text-green-700',
    rechazada: 'bg-red-100 text-red-700',
    cancelada: 'bg-red-50 text-red-500',
  };
  const cotStatusLabels = {
    draft: 'Borrador',
    enviada: 'Enviada',
    aprobada: 'Aprobada',
    rechazada: 'Rechazada',
    cancelada: 'Cancelada',
  };
  const histTypeColors = { DONE: 'bg-emerald-100 text-emerald-700', PENDING: 'bg-yellow-100 text-yellow-700' };
  const histTypeLabels = { DONE: 'Realizado', PENDING: 'Pendiente' };

  const fmtFecha = (ts) => ts ? new Date(ts)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtHora = (ts) => ts ? new Date(ts)?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';

  useEffect(() => { if (portalUser?.id) loadVehicles(); }, [portalUser?.id]);
  useEffect(() => { if (selectedVehicleId) loadHistory(selectedVehicleId); }, [selectedVehicleId]);

  const loadVehicles = async () => {
    setVehiclesLoading(true);
    try {
      const { data } = await supabase?.from('vehicles')?.select('id, marca, modelo, year, placas, vin')?.eq('client_id', portalUser?.id)?.order('created_at', { ascending: false });
      setVehicles(data || []);
      if (!selectedVehicleId && data?.length > 0) setSelectedVehicleId(data?.[0]?.id);
    } finally { setVehiclesLoading(false); }
  };

  const loadHistory = async (vehicleId) => {
    setLoading(true);
    try {
      const histResult = await supabase
        ?.from('vehicle_history')
        ?.select('id, title, notes, type, status, due_date, created_at, related_order_id, next_service_date, next_service_km, service_km_at_time, source_type')
        ?.eq('vehicle_id', vehicleId)
        ?.order('created_at', { ascending: false });

      const otsResult = await supabase
        ?.from('work_orders')
        ?.select('id, folio_ot, estado_ot, fecha_ingreso, motivo_ingreso, notas, kilometraje, total')
        ?.eq('vehicle_id', vehicleId)
        ?.order('fecha_ingreso', { ascending: false });

      const inspResult = await supabase
        ?.from('technical_inspections')
        ?.select('id, created_at, inspected_at, status, category, findings, recommendations, refaccion')
        ?.eq('vehicle_id', vehicleId)
        ?.order('created_at', { ascending: false });

      const otIds = otsResult?.data?.map(o => o?.id)?.filter(Boolean) || [];
      let quotesData = [];
      if (otIds?.length > 0) {
        const quotesResult = await supabase
          ?.from('quotations')
          ?.select('id, folio_cotizacion, status, created_at, total, notas, work_order_id')
          ?.in('work_order_id', otIds)
          ?.order('created_at', { ascending: false });
        quotesData = quotesResult?.data || [];
      }

      setVehicleHistory(histResult?.data || []);
      setWorkOrders(otsResult?.data || []);
      setInspections(inspResult?.data || []);
      setQuotations(quotesData);
    } finally { setLoading(false); }
  };

  if (vehiclesLoading) return <div className="flex items-center justify-center min-h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between py-1 mb-2 border-b border-gray-100">
        <button onClick={onRegresar} className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded">
          <ChevronLeft className="w-4 h-4" /><span>Regresar</span>
        </button>
      </div>
      <div><h1 className="text-2xl font-bold text-gray-900">Historial por Vehículo</h1><p className="text-gray-500 text-sm mt-0.5">Consulta el historial completo de servicios</p></div>
      {vehicles?.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Seleccionar vehículo</label>
          <div className="relative">
            <select value={selectedVehicleId} onChange={(e) => setSelectedVehicleId(e?.target?.value)} className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              <option value="">Seleccionar vehículo</option>
              {vehicles?.map(v => <option key={v?.id} value={v?.id}>{v?.marca} {v?.modelo} {v?.year ? `(${v?.year})` : ''} {v?.placas ? `· ${v?.placas}` : ''}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center"><Car className="w-10 h-10 text-gray-300 mx-auto mb-2" /><p className="text-gray-500 text-sm">No tienes vehículos registrados</p></div>
      )}

      {selectedVehicleId && (
        <>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
            {TABS?.map((tab, i) => (
              <button key={tab} onClick={() => setActiveTab(i)} className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${activeTab === i ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{tab}</button>
            ))}
          </div>
          {loading ? <div className="flex items-center justify-center min-h-32"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
            <div className="space-y-3">
              {activeTab === 0 && (vehicleHistory?.length === 0
                ? <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">Sin historial registrado</div>
                : vehicleHistory?.map(item => (
                  <div key={item?.id} className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900">{item?.title || '—'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtFecha(item?.created_at)}{fmtHora(item?.created_at) ? ` · ${fmtHora(item?.created_at)}` : ''}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${histTypeColors?.[item?.type] || 'bg-gray-100 text-gray-700'}`}>{histTypeLabels?.[item?.type] || item?.type || '—'}</span>
                    </div>
                    {item?.notes && <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">{item?.notes}</p>}
                    {(item?.next_service_date || item?.next_service_km) && (
                      <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                        <Wrench className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        <p className="text-xs text-blue-700 font-medium">
                          Próximo servicio recomendado:{' '}
                          {item?.next_service_date ? fmtFecha(item?.next_service_date) : ''}
                          {item?.next_service_date && item?.next_service_km ? ' · ' : ''}
                          {item?.next_service_km ? `${item?.next_service_km?.toLocaleString('es-MX')} km` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                ))
              )}

              {activeTab === 1 && (workOrders?.length === 0
                ? <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">Sin órdenes de trabajo</div>
                : workOrders?.map(ot => (
                  <div key={ot?.id} className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-blue-200 transition-colors" onClick={() => setSelectedOT(ot)}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">{(ot?.service_type || ot?.request_type || 'S')?.charAt(0)?.toUpperCase()}</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            {ot?.folio_ot || `OT-${ot?.id?.slice(0, 6)}`}
                          </p>
                          {ot?.description && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{ot?.description}</p>}
                          <p className="text-xs text-gray-400 mt-1">{fmtFecha(ot?.fecha_ingreso)}{fmtHora(ot?.fecha_ingreso) ? ` · ${fmtHora(ot?.fecha_ingreso)}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${estadoOTColors?.[ot?.estado_ot] || 'bg-gray-100 text-gray-700'}`}>{estadoOTLabels?.[ot?.estado_ot] || ot?.estado_ot || '—'}</span>
                        <Eye className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))
              )}

              {activeTab === 2 && (inspections?.length === 0
                ? <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">Sin inspecciones registradas</div>
                : inspections?.map(insp => (
                  <div key={insp?.id} className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900">Inspección técnica{insp?.category ? ` — ${insp?.category}` : ''}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtFecha(insp?.inspected_at || insp?.created_at)}{fmtHora(insp?.inspected_at || insp?.created_at) ? ` · ${fmtHora(insp?.inspected_at || insp?.created_at)}` : ''}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${inspStatusColors?.[insp?.status] || 'bg-gray-100 text-gray-700'}`}>{inspStatusLabels?.[insp?.status] || insp?.status || '—'}</span>
                        {insp?.findings && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Observaciones</p>
                            <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">{insp?.findings}</p>
                          </div>
                        )}
                        {insp?.recommendations && (
                          <div className="flex items-start gap-2 pt-1 border-t border-gray-100">
                            <Wrench className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-medium text-blue-700">Próximo servicio recomendado</p>
                              <p className="text-xs text-blue-600 mt-0.5">{insp?.recommendations}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}

              {activeTab === 3 && (quotations?.length === 0
                ? <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">Sin cotizaciones registradas</div>
                : quotations?.map(q => (
                  <div key={q?.id} className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900">{q?.folio_cotizacion || `COT-${q?.id?.slice(0, 6)}`}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtFecha(q?.created_at)}{fmtHora(q?.created_at) ? ` · ${fmtHora(q?.created_at)}` : ''}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${cotStatusColors?.[q?.status] || 'bg-gray-100 text-gray-700'}`}>{cotStatusLabels?.[q?.status] || q?.status || '—'}</span>
                        {q?.total != null && <p className="text-sm font-bold text-gray-900 mt-1">${Number(q?.total)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>}
                      </div>
                    </div>
                    {q?.notas && <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">{q?.notas}</p>}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {selectedOT && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Detalle de OT</h3>
              <button onClick={() => setSelectedOT(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-900">{selectedOT?.folio_ot || `OT-${selectedOT?.id?.slice(0, 6)}`}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${estadoOTColors?.[selectedOT?.estado_ot] || 'bg-gray-100 text-gray-700'}`}>{estadoOTLabels?.[selectedOT?.estado_ot] || selectedOT?.estado_ot || '—'}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Fecha de ingreso</p><p className="font-medium text-gray-800">{selectedOT?.fecha_ingreso ? new Date(selectedOT?.fecha_ingreso)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p></div>
                <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Hora</p><p className="font-medium text-gray-800">{fmtHora(selectedOT?.fecha_ingreso) || '—'}</p></div>
                {selectedOT?.kilometraje && <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Kilometraje</p><p className="font-medium text-gray-800">{selectedOT?.kilometraje?.toLocaleString('es-MX')} km</p></div>}
                {selectedOT?.total != null && <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Total</p><p className="font-bold text-gray-900">${Number(selectedOT?.total)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p></div>}
              </div>
              {selectedOT?.description && <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Descripción</p><p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selectedOT?.description}</p></div>}
              {selectedOT?.notas && <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Observaciones</p><p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selectedOT?.notas}</p></div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── NOTIFICACIONES VIEW ──────────────────────────────────────────────────────

const NotificacionesView = ({ portalUser, onRegresar }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (portalUser?.id) loadNotifications(); }, [portalUser?.id]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const clientId = portalUser?.id;
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const items = [];

      const { data: appts } = await supabase?.from('client_appointments')?.select('id, service_type, appointment_date, appointment_time, status')?.eq('client_id', clientId)?.gte('appointment_date', today)?.neq('status', 'cancelada')?.order('appointment_date', { ascending: true })?.limit(5);
      (appts || [])?.forEach(a => items?.push({ id: `appt-${a?.id}`, type: 'cita', title: 'Cita próxima', description: `${a?.service_type} · ${new Date(a.appointment_date + 'T12:00:00')?.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}${a?.appointment_time ? ` a las ${a?.appointment_time}` : ''}`, date: a?.appointment_date, read: false }));

      const { data: vehicles } = await supabase?.from('vehicles')?.select('id')?.eq('client_id', clientId);
      const vehicleIds = vehicles?.map(v => v?.id) || [];

      if (vehicleIds?.length > 0) {
        const { data: ots } = await supabase?.from('work_orders')?.select('id, folio_ot, status, updated_at, vehicles(marca, modelo)')?.in('vehicle_id', vehicleIds)?.in('status', ['listo', 'en_proceso', 'cotizado'])?.order('updated_at', { ascending: false })?.limit(5);
        (ots || [])?.forEach(ot => items?.push({ id: `ot-${ot?.id}`, type: ot?.status === 'listo' ? 'lista' : 'ot', title: ot?.status === 'listo' ? 'Unidad lista para entrega' : `OT ${ot?.status === 'cotizado' ? 'cotizada' : 'en proceso'}`, description: `${ot?.folio_ot || `OT-${ot?.id?.slice(0, 6)}`} · ${ot?.vehicles?.marca || ''} ${ot?.vehicles?.modelo || ''}`?.trim(), date: ot?.updated_at?.split('T')?.[0] || today, read: false }));

        const { data: quotes } = await supabase?.from('quotations')?.select('id, folio, status, created_at, vehicles(marca, modelo)')?.in('vehicle_id', vehicleIds)?.eq('status', 'pendiente_aprobacion')?.order('created_at', { ascending: false })?.limit(3);
        (quotes || [])?.forEach(q => items?.push({ id: `quote-${q?.id}`, type: 'cotizacion', title: 'Cotización pendiente de aprobación', description: `${q?.folio || `COT-${q?.id?.slice(0, 6)}`} · ${q?.vehicles?.marca || ''} ${q?.vehicles?.modelo || ''}`?.trim(), date: q?.created_at?.split('T')?.[0] || today, read: false }));
      }

      const { data: promos } = await supabase?.from('promotions')?.select('id, title, valid_until, created_at')?.eq('company_id', portalUser?.company_id)?.eq('is_active', true)?.limit(3);
      (promos || [])?.forEach(p => items?.push({ id: `promo-${p?.id}`, type: 'promocion', title: 'Promoción activa', description: `${p?.title}${p?.valid_until ? ` · Válida hasta ${new Date(p.valid_until + 'T12:00:00')?.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}` : ''}`, date: p?.created_at?.split('T')?.[0] || today, read: false }));

      items?.sort((a, b) => new Date(b.date) - new Date(a.date));
      setNotifications(items);
    } catch (err) { console.error('[NotificacionesView] Error:', err); } finally { setLoading(false); }
  };

  const markAsRead = (id) => setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, read: true } : n));
  const markAllRead = () => setNotifications(prev => prev?.map(n => ({ ...n, read: true })));
  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const iconMap = { cita: { icon: CalendarDays, bg: 'bg-blue-50', color: 'text-blue-600' }, lista: { icon: CheckCircle, bg: 'bg-green-50', color: 'text-green-600' }, cotizacion: { icon: FileText, bg: 'bg-yellow-50', color: 'text-yellow-600' }, promocion: { icon: Tag, bg: 'bg-purple-50', color: 'text-purple-600' }, ot: { icon: Clock, bg: 'bg-orange-50', color: 'text-orange-600' } };

  const fmtFecha = (ts) => ts ? new Date(ts)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  if (loading) return <div className="flex items-center justify-center min-h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between py-1 mb-2 border-b border-gray-100">
        <button onClick={onRegresar} className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded">
          <ChevronLeft className="w-4 h-4" /><span>Regresar</span>
        </button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notificaciones</h1>
          <p className="text-gray-500 text-sm mt-0.5">Manténte al día con las actualizaciones</p>
        </div>
        <button onClick={markAllRead} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
          <CheckCircle className="w-4 h-4" /><span className="hidden sm:inline">Marcar todas como leídas</span><span className="sm:hidden">Marcar como leídas</span>
        </button>
      </div>
      {unreadCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <p className="text-sm font-medium text-yellow-800">Tienes {unreadCount} notificaciones sin leer</p>
          </div>
          <button onClick={markAllRead} className="text-xs bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1.5 rounded-lg transition-colors font-medium">Marcar como leídas</button>
        </div>
      )}
      {notifications?.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No hay notificaciones</p>
          <p className="text-gray-400 text-sm mt-1">¡Estás al día!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications?.map((notification) => {
            const NotifIcon = iconMap?.[notification?.type]?.icon;
            return (
              <div key={notification?.id} className={`bg-white rounded-xl border p-4 flex items-center justify-between ${notification?.read ? 'hover:bg-gray-50' : 'border-blue-200 bg-blue-50/50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconMap?.[notification?.type]?.bg}`}>
                    {NotifIcon && <NotifIcon className={`w-4 h-4 ${iconMap?.[notification?.type]?.color}`} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{notification?.title}</p>
                    <p className="text-xs text-gray-500 truncate">{notification?.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <p className="text-xs text-gray-400">{fmtFecha(notification?.date)}</p>
                  <button onClick={() => markAsRead(notification?.id)} className="text-xs text-blue-600 hover:text-blue-800 transition-colors"><CheckCircle className="w-3 h-3" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── SOLICITUDES VIEW ───────────────────────────────────────────────────────

const SolicitudesView = ({ portalUser, onRegresar }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showCreateRequest, setShowCreateRequest] = useState(false);
  const [formData, setFormData] = useState({ ...VEHICLE_FORM_DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => { if (portalUser?.id) loadRequests(); }, [portalUser?.id]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase?.from('service_requests')?.select('*')?.eq('client_id', portalUser?.id)?.order('created_at', { ascending: false });
      if (err) throw err;
      setRequests(data || []);
    } catch (err) { console.error('[SolicitudesView] Error:', err); } finally { setLoading(false); }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors?.[field]) setFieldErrors(prev => { const n = { ...prev }; delete n?.[field]; return n; });
  };

  const handleRequestCreated = () => {
    setShowCreateRequest(false);
    loadRequests();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validateVehicleFields(formData);
    if (Object.keys(errs)?.length > 0) { setFieldErrors(errs); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        client_id: portalUser?.id,
        company_id: portalUser?.company_id,
        title: `${formData?.brand?.trim()} ${formData?.model?.trim()}`,
        description: formData?.vehicleType || '',
        plates: formData?.plates?.trim() || null,
        odometer: formData?.odometer ? parseInt(formData?.odometer, 10) : null,
        vin: formData?.vin?.trim() || null,
        status: 'nueva',
      };
      const { error: insertErr } = await supabase?.from('service_requests')?.insert(payload);
      if (insertErr) { setError('No se pudo crear la solicitud: ' + (insertErr?.message || 'Error desconocido')); return; }
      handleRequestCreated();
    } catch (err) {
      setError('Error inesperado: ' + (err?.message || 'Intenta de nuevo'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between py-1 mb-2 border-b border-gray-100">
        <button onClick={onRegresar} className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded">
          <ChevronLeft className="w-4 h-4" /><span>Regresar</span>
        </button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Solicitudes</h1>
          <p className="text-gray-500 text-sm mt-0.5">Gestiona tus solicitudes de servicio</p>
        </div>
        <button onClick={() => setShowCreateRequest(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
          <Plus className="w-4 h-4" /><span className="hidden sm:inline">Nueva solicitud</span><span className="sm:hidden">Agregar</span>
        </button>
      </div>

      {showCreateRequest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="text-base font-bold text-gray-900">Nueva solicitud</h2>
              <button onClick={() => setShowCreateRequest(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <VehicleFormUnified formData={formData} onChange={handleChange} errors={fieldErrors} />
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowCreateRequest(false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Guardando...</> : 'Guardar solicitud'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {requests?.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No tienes solicitudes registradas</p>
          <p className="text-gray-400 text-sm mt-1">Crea tu primera solicitud para comenzar</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {requests?.map((request) => (
            <div key={request?.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center"><Car className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="text-white font-bold text-base leading-tight">{request?.title || '—'}</p>
                    <p className="text-blue-100 text-sm">{request?.description || '—'}</p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Hash className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Placas</p><p className="text-sm font-semibold text-gray-900">{request?.plates || '—'}</p></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Gauge className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Kilometraje</p><p className="text-sm font-semibold text-gray-900">{request?.odometer ? `${Number(request?.odometer)?.toLocaleString('es-MX')} km` : '—'}</p></div>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">VIN</p>
                  <p className="text-xs font-mono text-gray-700 truncate">{request?.vin || '—'}</p>
                </div>
              </div>
              <div className="px-4 pb-4">
                <button onClick={() => setSelectedRequest(request)} className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors text-sm font-semibold">
                  <History className="w-4 h-4" />Ver detalle
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── SOLICITUD DETALLE VIEW ──────────────────────────────────────────────────

const SolicitudDetailView = ({ portalUser, initialRequestId, onRegresar }) => {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fmtFecha = (ts) => ts ? new Date(ts)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtHora = (ts) => ts ? new Date(ts)?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';

  useEffect(() => {
    if (portalUser?.id && initialRequestId) {
      loadRequest(initialRequestId);
    }
  }, [portalUser?.id, initialRequestId]);

  const loadRequest = async (requestId) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase?.from('service_requests')?.select('*')?.eq('id', requestId)?.eq('client_id', portalUser?.id)?.single();
      if (err) throw err;
      setRequest(data);
    } catch (err) {
      console.error('[SolicitudDetailView] Error:', err);
      setError('No se pudo cargar la solicitud.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  if (!request) return (
    <div className="flex flex-col items-center justify-center min-h-64 gap-4 p-6">
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
        <AlertCircle className="w-6 h-6 text-red-600" />
      </div>
      <div className="text-center">
        <p className="text-gray-800 font-semibold">Solicitud no encontrada</p>
        <p className="text-gray-500 text-sm mt-1">La solicitud que buscas no existe o no tienes permiso para verla.</p>
        <p className="text-xs text-gray-400 mt-2 font-mono">{error}</p>
      </div>
      <button
        onClick={() => { setError(''); loadRequest(initialRequestId); }}
        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
      >
        Intentar de nuevo
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between py-1 mb-2 border-b border-gray-100">
        <button onClick={onRegresar} className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded">
          <ChevronLeft className="w-4 h-4" /><span>Regresar</span>
        </button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Solicitud #{request?.id}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{request?.title}</p>
        </div>
        <button onClick={() => { setRequest(null); loadRequest(initialRequestId); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
          <Plus className="w-4 h-4" /><span className="hidden sm:inline">Nueva solicitud</span><span className="sm:hidden">Agregar</span>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Fecha de ingreso</p><p className="font-medium text-gray-800">{request?.created_at ? new Date(request?.created_at)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p></div>
          <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Hora</p><p className="font-medium text-gray-800">{fmtHora(request?.created_at) || '—'}</p></div>
          {request?.kilometraje && <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Kilometraje</p><p className="font-medium text-gray-800">{request?.kilometraje?.toLocaleString('es-MX')} km</p></div>}
          {request?.total != null && <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Total</p><p className="font-bold text-gray-900">${Number(request?.total)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p></div>}
        </div>
        {request?.description && <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Descripción</p><p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{request?.description}</p></div>}
        {request?.notas && <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Observaciones</p><p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{request?.notas}</p></div>}
      </div>
    </div>
  );
};

// ─── CITAS VIEW ─────────────────────────────────────────────────────────────

const CitasView = ({ portalUser, onRegresar }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateAppointment, setShowCreateAppointment] = useState(false);
  const [formData, setFormData] = useState({ ...VEHICLE_FORM_DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (portalUser?.id) loadAppointments();
  }, [portalUser?.id]);

  const loadAppointments = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase?.from('client_appointments')?.select('*')?.eq('client_id', portalUser?.id)?.order('appointment_date', { ascending: true });
      if (err) throw err;
      setAppointments(data || []);
    } catch (err) {
      console.error('[CitasView] Error:', err);
      setError('No se pudieron cargar las citas.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors?.[field]) setFieldErrors(prev => { const n = { ...prev }; delete n?.[field]; return n; });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validateVehicleFields(formData);
    if (Object.keys(errs)?.length > 0) { setFieldErrors(errs); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        client_id: portalUser?.id,
        company_id: portalUser?.company_id,
        service_type: formData?.vehicleType || 'Revisión general',
        appointment_date: new Date()?.toISOString()?.split('T')?.[0],
        status: 'pendiente',
      };
      const { error: insertErr } = await supabase?.from('client_appointments')?.insert(payload);
      if (insertErr) { setError('No se pudo crear la cita: ' + (insertErr?.message || 'Error desconocido')); return; }
      setShowCreateAppointment(false);
      loadAppointments();
    } catch (err) {
      setError('Error inesperado: ' + (err?.message || 'Intenta de nuevo'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between py-1 mb-2 border-b border-gray-100">
        <button onClick={onRegresar} className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded">
          <ChevronLeft className="w-4 h-4" /><span>Regresar</span>
        </button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Citas</h1>
          <p className="text-gray-500 text-sm mt-0.5">Gestiona tus citas de servicio</p>
        </div>
        <button onClick={() => setShowCreateAppointment(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
          <Plus className="w-4 h-4" /><span className="hidden sm:inline">Nueva cita</span><span className="sm:hidden">Agregar</span>
        </button>
      </div>

      {showCreateAppointment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="text-base font-bold text-gray-900">Nueva cita</h2>
              <button onClick={() => setShowCreateAppointment(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <VehicleFormUnified formData={formData} onChange={handleChange} errors={fieldErrors} />
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowCreateAppointment(false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Guardando...</> : 'Guardar cita'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {appointments?.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No tienes citas registradas</p>
          <p className="text-gray-400 text-sm mt-1">Programa tu primera cita para comenzar</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {appointments?.map((appointment) => (
            <div key={appointment?.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center"><Car className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="text-white font-bold text-base leading-tight">{appointment?.service_type || '—'}</p>
                    <p className="text-blue-100 text-sm">{appointment?.appointment_date || '—'}</p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Fecha</p><p className="text-sm font-semibold text-gray-900">{appointment?.appointment_date ? new Date(appointment?.appointment_date + 'T12:00:00')?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div><p className="text-[10px] text-gray-400 uppercase tracking-wide">Hora</p><p className="text-sm font-semibold text-gray-900">{appointment?.appointment_time || '—'}</p></div>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Estado</p>
                  <p className="text-xs text-gray-700">{appointment?.status || '—'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── APPOINTMENT DETAIL VIEW ──────────────────────────────────────────────────

const AppointmentDetailView = ({ portalUser, initialAppointmentId, onRegresar }) => {
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fmtFecha = (ts) => ts ? new Date(ts)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtHora = (ts) => ts ? new Date(ts)?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';

  useEffect(() => {
    if (portalUser?.id && initialAppointmentId) {
      loadAppointment(initialAppointmentId);
    }
  }, [portalUser?.id, initialAppointmentId]);

  const loadAppointment = async (appointmentId) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase?.from('client_appointments')?.select('*')?.eq('id', appointmentId)?.eq('client_id', portalUser?.id)?.single();
      if (err) throw err;
      setAppointment(data);
    } catch (err) {
      console.error('[AppointmentDetailView] Error:', err);
      setError('No se pudo cargar la cita.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  if (!appointment) return (
    <div className="flex flex-col items-center justify-center min-h-64 gap-4 p-6">
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
        <AlertCircle className="w-6 h-6 text-red-600" />
      </div>
      <div className="text-center">
        <p className="text-gray-800 font-semibold">Cita no encontrada</p>
        <p className="text-gray-500 text-sm mt-1">La cita que buscas no existe o no tienes permiso para verla.</p>
        <p className="text-xs text-gray-400 mt-2 font-mono">{error}</p>
      </div>
      <button
        onClick={() => { setError(''); loadAppointment(initialAppointmentId); }}
        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
      >
        Intentar de nuevo
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between py-1 mb-2 border-b border-gray-100">
        <button onClick={onRegresar} className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded">
          <ChevronLeft className="w-4 h-4" /><span>Regresar</span>
        </button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cita #{appointment?.id}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{appointment?.service_type}</p>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Fecha de cita</p><p className="font-medium text-gray-800">{appointment?.appointment_date ? new Date(appointment?.appointment_date + 'T12:00:00')?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p></div>
          <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Hora</p><p className="font-medium text-gray-800">{fmtHora(appointment?.appointment_date) || '—'}</p></div>
          <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Estado</p><p className="font-medium text-gray-800">{appointment?.status || '—'}</p></div>
          <div><p className="text-gray-400 uppercase tracking-wide mb-0.5">Tipo de servicio</p><p className="font-medium text-gray-800">{appointment?.service_type || '—'}</p></div>
        </div>
        {appointment?.notas && <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Observaciones</p><p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{appointment?.notas}</p></div>}
      </div>
    </div>
  );
};

// ─── MAIN PORTAL COMPONENT ────────────────────────────────────────────────────

const PortalClienteEmbedded = ({ initialPortalUser, onLogout }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [historialVehicleId, setHistorialVehicleId] = useState(null);
  const [solicitudDetailId, setSolicitudDetailId] = useState(null);
  const [citaDetailId, setCitaDetailId] = useState(null);

  const portalUser = initialPortalUser;

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <DashboardView portalUser={portalUser} setActiveView={setActiveView} onRegresar={onLogout} />;
      case 'vehiculos':
        return <VehiculosView portalUser={portalUser} setActiveView={setActiveView} setHistorialVehicleId={setHistorialVehicleId} onRegresar={() => setActiveView('dashboard')} />;
      case 'historial':
        return <HistorialView portalUser={portalUser} initialVehicleId={historialVehicleId} onRegresar={() => setActiveView('dashboard')} />;
      case 'notificaciones':
        return <NotificacionesView portalUser={portalUser} onRegresar={() => setActiveView('dashboard')} />;
      case 'solicitudes':
        return <SolicitudesView portalUser={portalUser} onRegresar={() => setActiveView('dashboard')} />;
      case 'solicitud-detalle':
        return <SolicitudDetailView portalUser={portalUser} initialRequestId={solicitudDetailId} onRegresar={() => setActiveView('solicitudes')} />;
      case 'citas':
        return <CitasView portalUser={portalUser} onRegresar={() => setActiveView('dashboard')} />;
      case 'cita-detalle':
        return <AppointmentDetailView portalUser={portalUser} initialAppointmentId={citaDetailId} onRegresar={() => setActiveView('citas')} />;
      default:
        return <DashboardView portalUser={portalUser} setActiveView={setActiveView} onRegresar={onLogout} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <Wrench className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900 leading-tight">Portal Cliente</h1>
              <p className="text-xs text-gray-500">{portalUser?.email}</p>
            </div>
          </div>
          <button onClick={onLogout} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
            Cerrar sesión
          </button>
        </div>

        <nav className="flex gap-1 mb-6 bg-white rounded-xl border border-gray-200 p-1 overflow-x-auto">
          {NAV_ITEMS?.map(item => {
            const Icon = item?.icon;
            return (
              <button
                key={item?.key}
                onClick={() => setActiveView(item?.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${activeView === item?.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {item?.label}
              </button>
            );
          })}
        </nav>

        <div>{renderView()}</div>
      </div>
    </div>
  );
};

export default PortalClienteEmbedded;